export type DraftPayload = {
  entry_mode: 'rego' | 'manual'
  rego: string | null
  state: string | null
  kilometres: number | null
  year: number | null
  make: string | null
  model: string | null
  name: string | null
  email: string
}

export type SavedDraft = DraftPayload & {
  id: string
  version: number
  reference: string
  details_complete: boolean
  saved_at: string
  journey_status?: string
  assessment_status?: string
  assessment_prepared?: boolean
  vehicle_lookup_status?: string
  pricing_status?: string
  estimated_range?: { id: string; lower: string; upper: string; currency: 'AUD'; generated_at: string } | null
  estimated_price?: { id: string; amount: string; currency: 'AUD'; generated_at: string } | null
  vehicle?: Record<string, string>
}

export type VehicleLookupResult = { data: SavedDraft; lookup: { status: string; error_code?: string; pricing_status: string; assessment_status?: string | null } }

type SaveRequest = { enquiry: DraftPayload; request_key: string; expected_version: number }
export type SaveState = {
  status: 'idle' | 'unsaved' | 'saving' | 'saved' | 'error' | 'conflict'
  draft: SavedDraft | null
  error: DraftApiError | null
}

const endpoint = '/api/acquisition/v1/public/enquiry'

export class DraftApiError extends Error {
  constructor(public status: number, public code: string, message: string, public errors?: Record<string, string | string[]>) {
    super(message)
    this.name = 'DraftApiError'
  }
}

async function draftRequest(method: 'GET' | 'PATCH', body?: string, signal?: AbortSignal, resume = false): Promise<SavedDraft | null> {
  const controller = new AbortController()
  const abort = () => controller.abort()
  const timer = window.setTimeout(abort, 15000)
  signal?.addEventListener('abort', abort, { once: true })
  if (signal?.aborted) controller.abort()
  try {
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
    if (method === 'PATCH' && !csrfToken) throw new DraftApiError(0, 'missing_csrf', 'Please refresh the page before saving your enquiry.')
    const response = await fetch(resume ? `${endpoint}?resume=1` : endpoint, {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
      headers: { Accept: 'application/json', ...(method === 'PATCH' ? { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken! } : {}) },
      ...(body ? { body } : {}),
    })
    const payload = await response.json().catch(() => null)
    if (!response.ok) throw new DraftApiError(response.status, payload?.code || 'request_failed', payload?.message || 'Your details could not be saved. Please try again.', payload?.errors)
    const draft = payload?.data
    if (draft === null && method === 'GET') return null
    if (!draft || typeof draft.id !== 'string' || !Number.isSafeInteger(draft.version) || draft.version < 0) throw new DraftApiError(0, 'invalid_response', method === 'GET' ? 'Your saved details could not be loaded. Please retry.' : 'The save could not be confirmed. Please retry.')
    return draft as SavedDraft
  } catch (error) {
    if (error instanceof DraftApiError) throw error
    throw new DraftApiError(0, 'connection_failed', method === 'GET' ? 'Your saved details could not be loaded. Please check your connection and retry.' : 'The save could not be confirmed. Check your connection and retry; your entered details are still here.')
  } finally {
    window.clearTimeout(timer)
    signal?.removeEventListener('abort', abort)
  }
}

export const loadDraft = (signal?: AbortSignal) => draftRequest('GET', undefined, signal)
export const loadCurrentDraft = () => draftRequest('GET', undefined, undefined, true)

export async function lookupVehicle(draft: SavedDraft, requestKey: string): Promise<VehicleLookupResult> {
  const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  if (!token) throw new DraftApiError(0, 'missing_csrf', 'Refresh this page before looking up your vehicle.')
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), 45000)
  try {
    const response = await fetch(`${endpoint}/lookup`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': token },
      body: JSON.stringify({ request_key: requestKey, expected_version: draft.version }),
    })
    const payload = await response.json().catch(() => null)
    if (!response.ok) throw new DraftApiError(response.status, payload?.code || 'lookup_failed', payload?.message || 'Your vehicle could not be looked up. Your enquiry is saved.')
    if (!payload?.data || payload.data.id !== draft.id || !Number.isSafeInteger(payload.data.version) || typeof payload?.lookup?.status !== 'string') throw new DraftApiError(0, 'invalid_response', 'The lookup could not be confirmed. Your enquiry is saved; please retry.')
    return payload as VehicleLookupResult
  } catch (error) {
    if (error instanceof DraftApiError) throw error
    throw new DraftApiError(0, 'lookup_unconfirmed', 'The lookup could not be confirmed. Your enquiry is saved; please retry.')
  } finally { window.clearTimeout(timer) }
}

export function assessmentRequestKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export async function startAssessment(requestKey: string): Promise<string> {
  const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
  if (!token) throw new Error('Refresh this page before starting your assessment.')
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), 15000)
  try {
    const response = await fetch(`${endpoint}/assessment`, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-CSRF-Token': token },
      body: JSON.stringify({ request_key: requestKey }),
    })
    const payload = await response.json().catch(() => null)
    if (!response.ok) throw new DraftApiError(response.status, payload?.code || 'request_failed', payload?.message || 'Your assessment could not be started. Please retry.')
    if (!payload?.data?.id || typeof payload.assessment_url !== 'string' || !payload.assessment_url.startsWith(`/assessment/${encodeURIComponent(payload.data.id)}?token=`)) throw new Error('The assessment could not be confirmed. Please retry.')
    return payload.assessment_url
  } catch (error) {
    if (error instanceof DraftApiError) throw error
    throw new Error('The assessment could not be confirmed. Check your connection and retry; your enquiry is saved.')
  } finally { window.clearTimeout(timer) }
}

export function draftPayload(draft: DraftPayload): DraftPayload {
  const text = (value: unknown) => String(value ?? '').trim() || null
  return {
    entry_mode: draft.entry_mode,
    rego: text(draft.rego)?.toUpperCase() || null,
    state: text(draft.state),
    kilometres: draft.kilometres === null || draft.kilometres === undefined ? null : Number(draft.kilometres),
    year: draft.year === null || draft.year === undefined ? null : Number(draft.year),
    make: text(draft.make), model: text(draft.model), name: text(draft.name), email: text(draft.email)?.toLowerCase() || '',
  }
}

const fingerprint = (payload: DraftPayload | null) => payload ? JSON.stringify(draftPayload(payload)) : null

function requestKey(): string {
  // getRandomValues works on the local HTTP host; randomUUID requires a secure context.
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), value => value.toString(16).padStart(2, '0')).join('')
}

/** Serialises writes and retains an ambiguous request verbatim until an explicit retry. */
export class DraftSaveQueue {
  private draft: SavedDraft | null
  private version: number
  private acknowledged: string | null
  private observed: DraftPayload | null
  private queued: DraftPayload | null = null
  private pending: { body: string; payload: DraftPayload } | null = null
  private running: Promise<void> | null = null
  private blocked: 'error' | 'validation' | 'conflict' | null = null
  private rejectedPayload: string | null = null
  private error: DraftApiError | null = null
  private disposed = false

  constructor(draft: SavedDraft | null, private onChange: (state: SaveState) => void) {
    this.draft = draft
    this.version = draft?.version ?? 0
    this.observed = draft ? draftPayload(draft) : null
    this.acknowledged = fingerprint(this.observed)
  }

  dispose() { this.disposed = true }

  confirmedDraft(): SavedDraft | null { return this.draft }

  acceptLookup(draft: SavedDraft) {
    if (this.disposed || this.pending || this.running || draft.id !== this.draft?.id || draft.version < this.version || fingerprint(draft) !== this.acknowledged) return false
    this.draft = draft
    this.version = draft.version
    this.emit()
    return true
  }

  observe(payload: DraftPayload | null) {
    if (fingerprint(payload) !== fingerprint(this.observed)) {
      this.observed = payload
      this.queued = null
    }
    if (this.blocked === 'validation' && fingerprint(payload) !== this.rejectedPayload) {
      this.blocked = null
      this.error = null
      this.rejectedPayload = null
    }
    this.emit()
  }

  async save(payload: DraftPayload): Promise<boolean> {
    this.observe(payload)
    this.queued = payload
    if (!this.blocked) await this.drain()
    this.emit()
    return !this.blocked && fingerprint(payload) === this.acknowledged
  }

  async retry(): Promise<void> {
    if (this.blocked !== 'error') return
    this.blocked = null
    this.error = null
    await this.drain()
  }

  private emit() {
    if (this.disposed) return
    const status = this.blocked === 'conflict' ? 'conflict'
      : this.blocked ? 'error'
      : this.pending || this.running ? 'saving'
      : this.acknowledged && this.acknowledged === fingerprint(this.observed) ? 'saved'
      : this.observed || this.draft ? 'unsaved' : 'idle'
    this.onChange({ status, draft: this.draft, error: this.error })
  }

  private drain(): Promise<void> {
    if (this.running) return this.running
    const execute = async () => {
      let refreshedVersion = false
      while (!this.disposed && !this.blocked && (this.pending || this.queued)) {
        if (!this.pending) {
          const payload = this.queued!
          this.queued = null
          if (fingerprint(payload) === this.acknowledged) continue
          const request: SaveRequest = { enquiry: payload, request_key: requestKey(), expected_version: this.version }
          this.pending = { body: JSON.stringify(request), payload }
        }
        const request = this.pending
        let receivedAcknowledgement = false
        this.emit()
        try {
          const saved = (await draftRequest('PATCH', request.body))!
          receivedAcknowledgement = true
          if (this.disposed) return
          if ((this.draft && saved.id !== this.draft.id) || fingerprint(saved) !== fingerprint(request.payload)) {
            throw new DraftApiError(409, 'stale_version', 'The saved enquiry has changed elsewhere. Your entered details have been kept here.')
          }
          this.draft = saved
          this.version = saved.version
          this.acknowledged = fingerprint(request.payload)
          this.pending = null
          this.error = null
        } catch (error) {
          if (this.disposed) return
          if (!receivedAcknowledgement && !refreshedVersion && error instanceof DraftApiError && error.status === 409 && error.code === 'stale_version' && this.draft) {
            refreshedVersion = true
            // Refresh the owned record without rotating its cookie. Retry only
            // if the customer fields are still the last acknowledged values.
            const current = await loadCurrentDraft()
            if (this.disposed) return
            if (current?.id === this.draft.id && current.version > this.version && fingerprint(current) === this.acknowledged) {
              this.draft = current
              this.version = current.version
              this.pending = { payload: request.payload, body: JSON.stringify({ enquiry: request.payload, request_key: requestKey(), expected_version: current.version }) }
              continue
            }
          }
          this.error = error instanceof DraftApiError ? error : new DraftApiError(0, 'save_failed', 'The save could not be confirmed. Please retry.')
          if (this.error.status === 409) this.blocked = 'conflict'
          else if (this.error.status === 422 && this.error.code === 'invalid_enquiry') {
            // A confirmed validation rejection did not write; a corrected payload may use a new key.
            this.pending = null
            this.rejectedPayload = fingerprint(request.payload)
            this.blocked = 'validation'
            if (this.queued && fingerprint(this.queued) !== this.rejectedPayload) {
              this.blocked = null
              this.error = null
              this.rejectedPayload = null
            }
          } else this.blocked = 'error'
        }
      }
    }
    this.running = execute().catch(error => {
      if (this.disposed) return
      this.error = error instanceof DraftApiError ? error : new DraftApiError(0, 'save_failed', 'The save could not be started. Please retry.')
      this.blocked = 'error'
      if (!this.pending) this.queued = this.observed
    }).finally(() => { this.running = null; this.emit() })
    this.emit()
    return this.running
  }
}
